import * as THREE from "three";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { VfxRadialStreakBatch } from "../../core/batches/vfx_radial_streak_batch.js";
import { VfxPlanarWaveBatch } from "../../core/batches/vfx_planar_wave_batch.js";
import { VfxBallisticStreakBatch } from "../../core/batches/vfx_ballistic_streak_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { randomRange } from "../../../common/common_funcs.js";
import { VfxGradientAtlas } from "../../core/textures/vfx_gradient_atlas.js";

export class VfxBigBallBlockImpactTask implements IVfxTask, IController
{
  // --- New 2D Sparks 1 Constants (VfxRadialStreakBatch) ---
  private static readonly SPARKS_1_BURST_COUNT             : number      = 6;
  private static readonly SPARKS_1_LIFESPAN                : number      = 0.35;
  private static readonly SPARKS_1_SPEED                   : number      = 4;
  private static readonly SPARKS_1_MIN_SPAWN_AREA_RADIUS   : number      = 0.6;
  private static readonly SPARKS_1_MAX_SPAWN_AREA_RADIUS   : number      = 0.8;
  private static readonly SPARKS_1_MIN_WIDTH               : number      = 2.2;
  private static readonly SPARKS_1_MAX_WIDTH               : number      = 3;
  private static readonly SPARKS_1_MIN_HEIGHT              : number      = 2;
  private static readonly SPARKS_1_MAX_HEIGHT              : number      = 2.5;
  private static readonly SPARKS_1_BRIGHTNESS_FADE_START_PROGRESS : number = 0.0; // Момент часу (0.0..1.0), коли починається спад яскравості
  private static readonly SPARKS_1_DISSOLVE_START_PROGRESS : number      = 0.1; // Момент часу (0.0..1.0), коли частинки починають розчинятися через CloudNoise
  private static readonly SPARKS_1_DISSOLVE_DIRECTION_WEIGHT: number     = 0.7; // Баланс розчинення між шумом (0.0) і напрямком уздовж частинки (1.0)
  private static readonly SPARKS_1_DISSOLVE_SOFTNESS       : number      = 0.1; // М'якість границі розчинення
  private static readonly SPARKS_1_COLOR_1                 : THREE.Color = new THREE.Color( 0xFFFF64 ); // Ядро (гарячий біло-жовтий)
  private static readonly SPARKS_1_COLOR_2                 : THREE.Color = new THREE.Color( 0xFFA03C ); // Середина (бурштиновий)
  private static readonly SPARKS_1_COLOR_3                 : THREE.Color = new THREE.Color( 0xFF6428 ); // Кромка/хвости (глибокий червоно-помаранчевий)

  // --- 3D Sparks 2 Constants (VfxBallisticStreakBatch) ---
  private static readonly SPARKS_2_BURST_COUNT             : number      = 20;
  private static readonly SPARKS_2_LIFESPAN                : number      = 0.45;
  private static readonly SPARKS_2_COLOR_1                 : THREE.Color = new THREE.Color( 0xFFFFC8 );
  private static readonly SPARKS_2_COLOR_2                 : THREE.Color = new THREE.Color( 0xFFB450 );
  private static readonly SPARKS_2_COLOR_3                 : THREE.Color = new THREE.Color( 0xFF5014 );
  private static readonly SPARKS_2_COLOR_2_PROGRESS        : number      = 0.5;
  private static readonly SPARKS_2_COLOR_3_PROGRESS        : number      = 0.7;
  private static readonly SPARKS_2_MIN_SPEED               : number      = 5;
  private static readonly SPARKS_2_MAX_SPEED               : number      = 8;
  private static readonly SPARKS_2_UP_MIN                  : number      = 5;
  private static readonly SPARKS_2_UP_MAX                  : number      = 7;
  private static readonly SPARKS_2_GRAVITY                 : number      = 24;
  private static readonly SPARKS_2_MIN_WIDTH               : number      = 0.56;
  private static readonly SPARKS_2_MAX_WIDTH               : number      = 0.62;
  private static readonly SPARKS_2_MIN_HEIGHT              : number      = 0.66;
  private static readonly SPARKS_2_MAX_HEIGHT              : number      = 0.72;
  private static readonly SPARKS_2_STRETCH_FACTOR          : number      = 0.05;
  private static readonly SPARKS_2_SHRINK_START_RATIO      : number      = 0.6;
  private static readonly SPARKS_2_SPAWN_AREA_RADIUS       : number      = 0.6;
  
  // --- Force Waves Constants (VfxPlanarWaveBatch) ---
  private static readonly FORCE_WAVE_BURST_COUNT          : number      = 6;
  private static readonly FORCE_WAVE_LIFESPAN             : number      = 0.25;
  private static readonly FORCE_WAVE_SPEED                : number      = 6;
  private static readonly FORCE_WAVE_MIN_SPAWN_AREA_RADIUS: number      = 0.2;
  private static readonly FORCE_WAVE_MAX_SPAWN_AREA_RADIUS: number      = 0.5;
  private static readonly FORCE_WAVE_MIN_WIDTH            : number      = 3.2;
  private static readonly FORCE_WAVE_MAX_WIDTH            : number      = 3.8;
  private static readonly FORCE_WAVE_MIN_HEIGHT           : number      = 1.4;
  private static readonly FORCE_WAVE_MAX_HEIGHT           : number      = 1.6;
  private static readonly FORCE_WAVE_FADE_START_PROGRESS  : number      = 0.4; // Момент часу (0.0..1.0), коли частинки починають втрачати альфу
  private static readonly FORCE_WAVE_MAX_ALPHA            : number      = 0.3;
  private static readonly FORCE_WAVE_COLOR                : THREE.Color = new THREE.Color( 0xFFFFFF );
  
  private static readonly TOTAL_LIFESPAN: number = Math.max(
    VfxBigBallBlockImpactTask.SPARKS_1_LIFESPAN,
    VfxBigBallBlockImpactTask.FORCE_WAVE_LIFESPAN,
    VfxBigBallBlockImpactTask.SPARKS_2_LIFESPAN
  );
  private static readonly PI2: number = Math.PI * 2.0;

  private static sparks_1_gradient_id: number = -1;
  private static sparks_2_gradient_id: number = -1;

  public is_active       : boolean = true;
  private position       : THREE.Vector3 = new THREE.Vector3();
  private start_time     : number;
  private has_spawned    : boolean = false;

  public constructor( position: THREE.Vector3 )
  {
    this.position.copy( position );
    this.start_time = Vfx3DRuntime.current_time;
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
  }

  public stop( _behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( _factor: number ): void {}

  private static readonly scratch_pos: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_vel: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_size: THREE.Vector2 = new THREE.Vector2();

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;
      this.spawnSparks();
      this.spawnForceWaves();
      this.spawnParabolicSparks();
    }

    if ( age >= VfxBigBallBlockImpactTask.TOTAL_LIFESPAN )
    {
      this.stop();
      return true;
    }

    return false;
  }

  private spawnSparks(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxRadialStreakBatch );

    if ( VfxBigBallBlockImpactTask.sparks_1_gradient_id === -1 )
    {
      VfxBigBallBlockImpactTask.sparks_1_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.1, color: VfxBigBallBlockImpactTask.SPARKS_1_COLOR_3 },
        { stop: 0.5, color: VfxBigBallBlockImpactTask.SPARKS_1_COLOR_2 },
        { stop: 1, color: VfxBigBallBlockImpactTask.SPARKS_1_COLOR_1 },
      ]);
    }

    const count = VfxBigBallBlockImpactTask.SPARKS_1_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( i / count ) * VfxBigBallBlockImpactTask.PI2 + randomRange( -0.1, 0.1 );
      const cosAngle = Math.cos( angle );
      const sinAngle = Math.sin( angle );
      const spawn_dist = randomRange( VfxBigBallBlockImpactTask.SPARKS_1_MIN_SPAWN_AREA_RADIUS, VfxBigBallBlockImpactTask.SPARKS_1_MAX_SPAWN_AREA_RADIUS );

      VfxBigBallBlockImpactTask.scratch_pos.set(
        this.position.x + cosAngle * spawn_dist,
        this.position.y + sinAngle * spawn_dist,
        this.position.z
      );

      VfxBigBallBlockImpactTask.scratch_vel.set(
        cosAngle * VfxBigBallBlockImpactTask.SPARKS_1_SPEED,
        sinAngle * VfxBigBallBlockImpactTask.SPARKS_1_SPEED,
        0.0
      );

      const width  = randomRange( VfxBigBallBlockImpactTask.SPARKS_1_MIN_WIDTH, VfxBigBallBlockImpactTask.SPARKS_1_MAX_WIDTH );
      const height = randomRange(VfxBigBallBlockImpactTask.SPARKS_1_MIN_HEIGHT, VfxBigBallBlockImpactTask.SPARKS_1_MAX_HEIGHT);
      const res_id = Math.random() < 0.5 ? ResourceID.ShockwaveGlow2 : ResourceID.ShockwaveGlow3;

      batch.spawnParticle(
        res_id,
        VfxBigBallBlockImpactTask.sparks_1_gradient_id,
        VfxBigBallBlockImpactTask.scratch_pos,
        VfxBigBallBlockImpactTask.scratch_vel,
        width,
        height,
        VfxBigBallBlockImpactTask.SPARKS_1_BRIGHTNESS_FADE_START_PROGRESS,
        VfxBigBallBlockImpactTask.SPARKS_1_DISSOLVE_START_PROGRESS,
        this.start_time,
        VfxBigBallBlockImpactTask.SPARKS_1_LIFESPAN,
        1,
        VfxBigBallBlockImpactTask.SPARKS_1_DISSOLVE_DIRECTION_WEIGHT,
        VfxBigBallBlockImpactTask.SPARKS_1_DISSOLVE_SOFTNESS,
      );
    }
  }

  private spawnForceWaves(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxPlanarWaveBatch );

    const count = VfxBigBallBlockImpactTask.FORCE_WAVE_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      // Рівномірний радіальний розліт у 2D площині (XY) зі зміщенням фази відносно іскор
      const angle = ( ( i + 0.5 ) / count ) * VfxBigBallBlockImpactTask.PI2 + randomRange( -0.1, 0.1 );
      const cosAngle = Math.cos( angle );
      const sinAngle = Math.sin( angle );
      const spawn_dist = randomRange(
        VfxBigBallBlockImpactTask.FORCE_WAVE_MIN_SPAWN_AREA_RADIUS,
        VfxBigBallBlockImpactTask.FORCE_WAVE_MAX_SPAWN_AREA_RADIUS
      );

      VfxBigBallBlockImpactTask.scratch_pos.set(
        this.position.x + cosAngle * spawn_dist,
        this.position.y + sinAngle * spawn_dist,
        this.position.z
      );

      // Швидкість суворо в 2D площині XY (z = 0, без падіння/гравітації)
      VfxBigBallBlockImpactTask.scratch_vel.set(
        cosAngle * VfxBigBallBlockImpactTask.FORCE_WAVE_SPEED,
        sinAngle * VfxBigBallBlockImpactTask.FORCE_WAVE_SPEED,
        0.0
      );

      const width  = randomRange( VfxBigBallBlockImpactTask.FORCE_WAVE_MIN_WIDTH, VfxBigBallBlockImpactTask.FORCE_WAVE_MAX_WIDTH );
      const height = randomRange( VfxBigBallBlockImpactTask.FORCE_WAVE_MIN_HEIGHT, VfxBigBallBlockImpactTask.FORCE_WAVE_MAX_HEIGHT );

      // Рандомний вибір текстури між WaveGlow1 та WaveGlow2
      const res_id = Math.random() < 0.5 ? ResourceID.WaveGlow1 : ResourceID.WaveGlow2;

      batch.spawnParticle(
        res_id,
        VfxBigBallBlockImpactTask.scratch_pos,
        VfxBigBallBlockImpactTask.scratch_vel,
        width,
        height,
        VfxBigBallBlockImpactTask.FORCE_WAVE_FADE_START_PROGRESS,
        this.start_time,
        VfxBigBallBlockImpactTask.FORCE_WAVE_LIFESPAN,
        VfxBigBallBlockImpactTask.FORCE_WAVE_MAX_ALPHA,
        VfxBigBallBlockImpactTask.FORCE_WAVE_COLOR
      );
    }
  }



  private spawnParabolicSparks(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxBallisticStreakBatch );

    if ( VfxBigBallBlockImpactTask.sparks_2_gradient_id === -1 )
    {
      VfxBigBallBlockImpactTask.sparks_2_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxBigBallBlockImpactTask.SPARKS_2_COLOR_1 },
        { stop: VfxBigBallBlockImpactTask.SPARKS_2_COLOR_2_PROGRESS, color: VfxBigBallBlockImpactTask.SPARKS_2_COLOR_2 },
        { stop: VfxBigBallBlockImpactTask.SPARKS_2_COLOR_3_PROGRESS, color: VfxBigBallBlockImpactTask.SPARKS_2_COLOR_3 },
        { stop: 1.0, color: new THREE.Color( 0x000000 ) }
      ]);
    }

    const count = VfxBigBallBlockImpactTask.SPARKS_2_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( i / count ) * VfxBigBallBlockImpactTask.PI2 + randomRange( -0.2, 0.2 );
      const cosAngle = Math.cos( angle );
      const sinAngle = Math.sin( angle );
      const spawn_dist = randomRange( 0, VfxBigBallBlockImpactTask.SPARKS_2_SPAWN_AREA_RADIUS );
      
      VfxBigBallBlockImpactTask.scratch_pos.set(
        this.position.x + cosAngle * spawn_dist,
        this.position.y + sinAngle * spawn_dist,
        this.position.z + 0.01
      );

      const speed = randomRange(
        VfxBigBallBlockImpactTask.SPARKS_2_MIN_SPEED,
        VfxBigBallBlockImpactTask.SPARKS_2_MAX_SPEED
      );
      const up_speed = randomRange(
        VfxBigBallBlockImpactTask.SPARKS_2_UP_MIN,
        VfxBigBallBlockImpactTask.SPARKS_2_UP_MAX
      );
      VfxBigBallBlockImpactTask.scratch_vel.set(
        cosAngle * speed,
        sinAngle * speed + up_speed,
        0.0
      );

      const width = randomRange(
        VfxBigBallBlockImpactTask.SPARKS_2_MIN_WIDTH,
        VfxBigBallBlockImpactTask.SPARKS_2_MAX_WIDTH
      );
      const height = randomRange(
        VfxBigBallBlockImpactTask.SPARKS_2_MIN_HEIGHT,
        VfxBigBallBlockImpactTask.SPARKS_2_MAX_HEIGHT
      );
      VfxBigBallBlockImpactTask.scratch_size.set( width, height );

      batch.spawnParticle(
        ResourceID.EllipseGlow1,
        VfxBigBallBlockImpactTask.sparks_2_gradient_id,
        VfxBigBallBlockImpactTask.scratch_pos,
        VfxBigBallBlockImpactTask.scratch_vel,
        VfxBigBallBlockImpactTask.SPARKS_2_GRAVITY,
        VfxBigBallBlockImpactTask.scratch_size,
        VfxBigBallBlockImpactTask.SPARKS_2_STRETCH_FACTOR,
        VfxBigBallBlockImpactTask.SPARKS_2_SHRINK_START_RATIO,
        this.start_time,
        VfxBigBallBlockImpactTask.SPARKS_2_LIFESPAN
      );
    }
  }
}
